import {
    faRefresh,
    faUpRightFromSquare,
} from "@fortawesome/pro-light-svg-icons";
import {
    faAngleDown,
    faAngleUp,
    faPlus,
    faTriangleExclamation,
    faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "@tanstack/react-store";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { Search, VaporIcon } from "@vapor/react-icons";
import {
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    useTheme,
} from "@vapor/react-material";
import type { ChangeEvent, ReactElement } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import { ADE_URL } from "../../../../../config";
import { useGetKPIs } from "../../../../../core/api/use-get-kpis/useGetKPIs";
import {
    TaxSubmissionStatus,
    WARNING_SENT_STATUSES,
} from "../../../../../core/dtos/TaxSubmissionStatus";
import { store } from "../../../../../store/store";
import { taxSubmissionsStoreSlice } from "../../../../../store/taxSubmissionSearchFiltersStoreSlice";
import { ExpiringIdentitiesWarnings } from "../expiring-identities-warnings/ExpiringIdentitiesWarnings";
import { TaxSubmissionsTableHeaderFilter } from "../tax-submissions-table-header-filter/TaxSubmissionsTableHeaderFilter";
import { TaxSubmissionsTableHeaderKPIs } from "../tax-submissions-table-header-kpis/TaxSubmissionsTableHeaderKPIs";

const REFRESHABLE_QUERY_KEYS = [
    "useGetTaxSubmissions",
    "useGetKPIs",
    "useGetSubjects",
    "useGetTaxPayers",
    "useGetExpiringIdentities",
] as const;

interface TaxSubmissionsTableHeaderProps {
    uploadDisabled: boolean;
    onClickUploadTaxSubmission: (value: boolean) => void;
}

export const TaxSubmissionsTableHeader = ({
    uploadDisabled,
    onClickUploadTaxSubmission,
}: TaxSubmissionsTableHeaderProps): ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();

    const queryClient = useQueryClient();
    const isRefreshing =
        useIsFetching({
            predicate: (q) =>
                REFRESHABLE_QUERY_KEYS.includes(
                    q.queryKey[0] as (typeof REFRESHABLE_QUERY_KEYS)[number],
                ),
        }) > 0;

    const refreshData = useCallback(() => {
        REFRESHABLE_QUERY_KEYS.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: [key] });
        });
    }, [queryClient]);

    const filters = useSelector(
        store,
        (state) => state.taxSubmissionsSearchFilters,
    );

    const fullTextSearchInputRef = useRef<HTMLInputElement>(null);

    const kpis = useGetKPIs().data;

    const numberOfExpired = useMemo(() => {
        const expired = kpis.find(
            (kpi) => kpi.status === TaxSubmissionStatus.EXPIRED,
        );
        return expired?.numberOfTaxSubmissions || 0;
    }, [kpis]);

    const numberOfWarnings = useMemo(() => {
        const warnings = kpis.reduce((acc, kpi) => {
            if (WARNING_SENT_STATUSES.includes(kpi.status)) {
                acc += kpi.numberOfTaxSubmissions;
            }
            return acc;
        }, 0);

        return warnings;
    }, [kpis]);

    const shouldShowExpired = useMemo(() => {
        return numberOfExpired > 0 || filters.expired;
    }, [numberOfExpired, filters.expired]);

    const shouldShowWarning = useMemo(() => {
        const hasSent = filters.statuses.includes(TaxSubmissionStatus.SENT);
        const hasWarnings = filters.statuses.some((status) =>
            WARNING_SENT_STATUSES.includes(status),
        );

        return (numberOfWarnings > 0 && hasSent) || hasWarnings;
    }, [numberOfWarnings, filters.statuses]);

    const [openFilters, setOpenFilters] = useState(false);

    const isSomeFilterSet = useMemo(() => {
        return filters.intermediary ||
            filters.operator ||
            filters.taxPayer ||
            filters.statuses.length > 0 ||
            filters.type ||
            filters.fullTextSearch ||
            filters.expired === true
            ? true
            : false;
    }, [
        filters.intermediary,
        filters.operator,
        filters.taxPayer,
        filters.statuses.length,
        filters.type,
        filters.fullTextSearch,
        filters.expired,
    ]);

    const debouncedOnFilterChange = useDebouncedCallback((cb: () => void) => {
        cb();
    }, 500);

    const handleFullTextSearchChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const fullTextSearch = event.target.value;
            const regex = new RegExp("^[a-zA-Z0-9\\s._()\\[\\]-]+$");

            if (!!fullTextSearch && !regex.test(fullTextSearch)) {
                const sanitized = fullTextSearch.replace(
                    /[^a-zA-Z0-9\s._()[\]-]/g,
                    "",
                );
                event.target.value = sanitized;
            } else {
                debouncedOnFilterChange(() => {
                    taxSubmissionsStoreSlice.updateValues({
                        fullTextSearch: event.target.value,
                    });
                });
            }
        },
        [debouncedOnFilterChange],
    );

    const handleResetFilters = useCallback(() => {
        if (fullTextSearchInputRef.current) {
            fullTextSearchInputRef.current.value = "";
        }
        taxSubmissionsStoreSlice.resetValues();
    }, []);

    const handleToggleFilters = useCallback(() => {
        setOpenFilters((previous) => !previous);
    }, []);

    return (
        <Stack spacing={2}>
            <ExpiringIdentitiesWarnings />
            <TaxSubmissionsTableHeaderKPIs
                accordionRight={
                    <Stack direction="row" spacing={1}>
                        <Button
                            onClick={() => {
                                window.open(ADE_URL, "_blank", "noreferrer");
                            }}
                            startIcon={
                                <VaporIcon
                                    color="primary"
                                    icon={faUpRightFromSquare}
                                ></VaporIcon>
                            }
                            variant="outlined"
                        >
                            <Typography
                                color={theme.palette.primary.interactiveDefault}
                                variant="bodyLarge500"
                            >
                                {t(
                                    "views.home.components.tax_submissions_table_header.filters.access_ade_url",
                                )}
                            </Typography>
                        </Button>
                        <Button
                            disabled={uploadDisabled}
                            onClick={() => onClickUploadTaxSubmission(true)}
                            startIcon={
                                <VaporIcon icon={faPlus} color="white" />
                            }
                            variant="contained"
                        >
                            <Typography
                                color={theme.palette.primary.ghostWhite}
                                variant="bodyLarge500"
                            >
                                {t(
                                    "views.home.components.tax_submissions_table_header.accordion.upload_taxSubmission",
                                )}
                            </Typography>
                        </Button>
                    </Stack>
                }
            />
            <TaxSubmissionsTableHeaderFilter
                accordionLeft={
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            placeholder={t(
                                "views.home.components.tax_submissions_table_header.filters.fulfilment",
                            )}
                            sx={{ width: 350 }}
                            onChange={handleFullTextSearchChange}
                            inputRef={fullTextSearchInputRef}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            sx={{
                                                pointerEvents: "none",
                                                background:
                                                    "linear-gradient(-26.56505117707799deg, rgb(0, 195, 234) 0%, rgb(0, 144, 209) 100%)",
                                            }}
                                        >
                                            <Search color="white" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {shouldShowExpired && (
                            <Chip
                                label={t(
                                    "views.home.components.tax_submissions_table_header.expired_tax_submissions",
                                    {
                                        number: numberOfExpired,
                                    },
                                )}
                                onClick={() => {
                                    taxSubmissionsStoreSlice.updateValues({
                                        expired: !filters.expired,
                                    });
                                }}
                                variant={
                                    filters.expired ? "outlined" : "filled"
                                }
                                clickable
                            ></Chip>
                        )}
                        {shouldShowWarning && (
                            <Chip
                                label={t(
                                    "views.home.components.tax_submissions_table_header.sent_with_warnings",
                                    {
                                        number: numberOfWarnings,
                                    },
                                )}
                                icon={
                                    <VaporIcon
                                        icon={faTriangleExclamation}
                                        color="warning"
                                    />
                                }
                                onClick={() => {
                                    const currentStatuses = filters.statuses;
                                    const hasWarnings = currentStatuses.some(
                                        (status) =>
                                            WARNING_SENT_STATUSES.includes(
                                                status,
                                            ),
                                    );
                                    const hasSent = currentStatuses.includes(
                                        TaxSubmissionStatus.SENT,
                                    );

                                    if (hasWarnings && hasSent) {
                                        // If both are present, keep only WARNING_SENT_STATUSES
                                        taxSubmissionsStoreSlice.updateValues({
                                            statuses: currentStatuses.filter(
                                                (status) =>
                                                    status !==
                                                    TaxSubmissionStatus.SENT,
                                            ),
                                        });
                                    } else if (hasWarnings && !hasSent) {
                                        // If only WARNING_SENT_STATUSES, add SENT
                                        taxSubmissionsStoreSlice.updateValues({
                                            statuses: [
                                                ...currentStatuses,
                                                TaxSubmissionStatus.SENT,
                                            ],
                                        });
                                    }
                                }}
                                variant={
                                    filters.statuses.some((status) =>
                                        WARNING_SENT_STATUSES.includes(status),
                                    ) &&
                                    !filters.statuses.includes(
                                        TaxSubmissionStatus.SENT,
                                    )
                                        ? "outlined"
                                        : "filled"
                                }
                                clickable
                            ></Chip>
                        )}
                        <Button
                            startIcon={
                                <VaporIcon
                                    color="primary"
                                    icon={openFilters ? faAngleUp : faAngleDown}
                                />
                            }
                            onClick={handleToggleFilters}
                        >
                            <Typography
                                color={theme.palette.primary.richElectricBlue}
                                variant="bodyLarge500"
                            >
                                {t(
                                    "views.home.components.tax_submissions_table_header_filters.accordion.others_filters",
                                )}
                            </Typography>
                        </Button>
                        <Button
                            disabled={!isSomeFilterSet}
                            onClick={handleResetFilters}
                            startIcon={
                                <VaporIcon
                                    color={
                                        isSomeFilterSet ? "primary" : "disabled"
                                    }
                                    icon={faXmark}
                                />
                            }
                        >
                            <Typography
                                color={
                                    isSomeFilterSet
                                        ? theme.palette.primary.richElectricBlue
                                        : theme.palette.primary.textHintColor
                                }
                                variant="bodyLarge500"
                            >
                                {t(
                                    "views.home.components.tax_submissions_table_header.filters.reset",
                                )}
                            </Typography>
                        </Button>
                    </Stack>
                }
                accordionRight={
                    <Stack direction="row" spacing={1}>
                        <Button disabled={isRefreshing} onClick={refreshData}>
                            <Tooltip title={t("views.home.refresh")}>
                                <VaporIcon
                                    icon={faRefresh}
                                    color={
                                        isRefreshing ? "disabled" : "primary"
                                    }
                                />
                            </Tooltip>
                        </Button>
                    </Stack>
                }
                openFilters={openFilters}
            />
        </Stack>
    );
};
